import type { FeatureFlags, OpenUrl } from './types';
import type { WebViewMessageEvent } from 'react-native-webview';

interface AnalyticsConfig {
  platform: 'Android' | 'iOS';
  osName: string;
  deviceModel: string;
  appVersion: string;
}

function isAnalyticsConfig(value: unknown): value is AnalyticsConfig {
  if (!value || typeof value !== 'object') return false;
  const config = value as Record<string, unknown>;
  return (
    (config.platform === 'Android' || config.platform === 'iOS') &&
    typeof config.osName === 'string' &&
    typeof config.deviceModel === 'string' &&
    typeof config.appVersion === 'string'
  );
}

/**
 * Native screen types that can be navigated to from WebView
 */
export type NativeScreen =
  | { screen: 'ProductDetail'; params: { productSlug: string } }
  | { screen: 'Home'; params?: undefined };

export type ActorIdPayload = {
  actorId: string;
  sessionId: string;
  sessionTimestamp: number;
};

export type BridgeApiConstructorType = {
  devDebugConsole: (value: string) => void;
  featureFlags?: FeatureFlags;
  onCookieDump?: (payload: { url?: string; domain: string; cookies: string }) => void | Promise<void>;
  onCartUpdated?: () => void | Promise<void>;
  onUserLoggedIn?: () => void | Promise<void>;
  onUserLoggedOut?: () => void | Promise<void>;
  onScrollDetected?: (scrollY: number) => void | Promise<void>;
  onCountryChange?: (country: string) => void | Promise<void>;
  onCartOpen?: () => void | Promise<void>;
  onNavigateToNativeScreen?: (screenData: NativeScreen) => void | Promise<void>;
  onActorIdResolved?: (payload: ActorIdPayload) => void | Promise<void>;
};

export class BridgeApi {
  private devDebugConsole: (value: string) => void;
  private featureFlags: FeatureFlags;
  private onCartUpdated?: () => void | Promise<void>;
  private onUserLoggedIn?: () => void | Promise<void>;
  private onUserLoggedOut?: () => void | Promise<void>;
  private onScrollDetected?: (scrollY: number) => void | Promise<void>;
  private onCountryChange?: (country: string) => void | Promise<void>;
  private onCartOpen?: () => void | Promise<void>;
  private onNavigateToNativeScreen?: (screenData: NativeScreen) => void | Promise<void>;
  private onActorIdResolved?: (payload: ActorIdPayload) => void | Promise<void>;

  constructor({
    devDebugConsole,
    featureFlags,
    onCartUpdated,
    onUserLoggedIn,
    onUserLoggedOut,
    onScrollDetected,
    onCountryChange,
    onCartOpen,
    onNavigateToNativeScreen,
    onActorIdResolved,
  }: BridgeApiConstructorType) {
    this.devDebugConsole = devDebugConsole;
    this.featureFlags = featureFlags || {
      callingEnabled: false,
    };
    this.onCartUpdated = onCartUpdated;
    this.onUserLoggedIn = onUserLoggedIn;
    this.onUserLoggedOut = onUserLoggedOut;
    this.onScrollDetected = onScrollDetected;
    this.onCountryChange = onCountryChange;
    this.onCartOpen = onCartOpen;
    this.onNavigateToNativeScreen = onNavigateToNativeScreen;
    this.onActorIdResolved = onActorIdResolved;
  }

  private sendMessageToRN = (action: string, payload?: unknown) => {
    const message = JSON.stringify({ action, payload });
    this.devDebugConsole(`Sending to native: ${message}`);

    // Use postMessage provided by React Native WebView
    const windowWithRN = window as Window & {
      ReactNativeWebView?: {
        postMessage: (message: string) => void;
      };
    };

    if (
      typeof window !== 'undefined' &&
      windowWithRN.ReactNativeWebView &&
      windowWithRN.ReactNativeWebView.postMessage
    ) {
      windowWithRN.ReactNativeWebView.postMessage(message);
    } else {
      console.warn('ReactNativeWebView.postMessage is not available');
    }
  };

  private static sendMessageToRNStatic = (action: string, payload?: unknown) => {
    const message = JSON.stringify({ action, payload });

    // Use postMessage provided by React Native WebView
    const windowWithRN = window as Window & {
      ReactNativeWebView?: {
        postMessage: (message: string) => void;
      };
    };

    if (
      typeof window !== 'undefined' &&
      windowWithRN.ReactNativeWebView &&
      windowWithRN.ReactNativeWebView.postMessage
    ) {
      windowWithRN.ReactNativeWebView.postMessage(message);
    }
  };

  public isFeatureEnabled(feature: keyof FeatureFlags): boolean {
    return this.featureFlags[feature] || false;
  }

  public updateFeatureFlags(flags: Partial<FeatureFlags>): void {
    this.featureFlags = { ...this.featureFlags, ...flags };
  }

  public openUrl(data: OpenUrl): void {
    this.sendMessageToRN('openUrl', data);
  }

  public goBack(): void {
    this.sendMessageToRN('goBack');
  }

  public reload(): void {
    this.sendMessageToRN('reload');
  }

  public static cartUpdated(): void {
    this.sendMessageToRNStatic('cartUpdated');
  }

  public static cartOpen(): void {
    this.sendMessageToRNStatic('cartOpen');
  }

  public static userLoggedOut(): void {
    this.sendMessageToRNStatic('userLoggedOut');
  }

  /**
   * Notify native that user logged in and wait for native operations to complete
   * Returns a Promise that resolves when native side completes all auth-related operations
   * Note: Caller must check isWebview() before calling this method
   */
  public static userLoggedIn(): Promise<void> {
    return new Promise((resolve) => {
      const windowWithHandler = window as Window & {
        __ssUserLoggedInComplete?: () => void;
      };

      windowWithHandler.__ssUserLoggedInComplete = () => {
        delete windowWithHandler.__ssUserLoggedInComplete;
        resolve();
      };

      this.sendMessageToRNStatic('userLoggedIn');
    });
  }

  /**
   * Notify native app that actorId has been resolved/changed.
   * Called by PersonalizationAuthSync when actorId changes in WebView.
   * Native side saves the new identity to AsyncStorage + CookieManager.
   *
   * Fire-and-forget — no callback needed (AsyncStorage write is fast).
   */
  public static actorIdResolved(payload: ActorIdPayload): void {
    this.sendMessageToRNStatic('actorIdResolved', payload);
  }

  /**
   * Navigate to a native screen from WebView
   * Use this for all native screen navigations
   */
  public static navigateToNativeScreen(screenData: NativeScreen): void {
    this.sendMessageToRNStatic('navigateToNativeScreen', screenData);
  }

  public static isWebview = () => {
    return (
      typeof window !== 'undefined' &&
      (window as Window & { ReactNativeWebView?: { postMessage: (message: string) => void } }).ReactNativeWebView
    );
  };

  /**
   * Parse URL and return native screen data if it should be handled by native app
   * Returns null if URL should be handled by web
   */
  public static parseUrlToNativeScreen = (url: string): NativeScreen | null => {
    try {
      // Extract pathname from URL - handle both absolute and relative URLs
      let pathname = '/';

      if (url.startsWith('http://') || url.startsWith('https://')) {
        // Absolute URL - use URL constructor
        const parsed = new URL(url);
        pathname = parsed.pathname || '/';
      } else if (url.startsWith('/')) {
        // Relative URL starting with /
        pathname = url.split('?')[0] || '/';
      } else {
        return null;
      }

      const normalizedPath = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;

      // Home screen
      if (normalizedPath === '/') {
        return { screen: 'Home' };
      }

      // Product detail screen
      if (normalizedPath.startsWith('/product/')) {
        const productSlug = normalizedPath.replace('/product/', '');
        return { screen: 'ProductDetail', params: { productSlug } };
      }

      return null;
    } catch {
      return null;
    }
  };

  /**
   * Try to navigate to native screen if in WebView and URL matches a native screen
   * Returns true if handled by native, false if should proceed with web navigation
   */
  public static tryNavigateToNativeScreen = (href: string): boolean => {
    const screenData = BridgeApi.parseUrlToNativeScreen(href);
    if (screenData) {
      BridgeApi.navigateToNativeScreen(screenData);
      return true;
    }
    return false;
  };

  public async handleNativeMessage(message: WebViewMessageEvent): Promise<void> {
    try {
      const data = JSON.parse(message.nativeEvent.data);
      const { action, payload } = data;

      switch (action) {
        case 'cartUpdated': {
          try {
            if (this.onCartUpdated) {
              void this.onCartUpdated();
            }
          } catch (e) {
            console.warn('onCartUpdated handler threw', e);
          }
          break;
        }

        case 'userLoggedIn': {
          try {
            if (this.onUserLoggedIn) {
              await this.onUserLoggedIn();
            }
          } catch (e) {
            console.warn('onUserLoggedIn handler threw', e);
          }
          break;
        }

        case 'userLoggedOut': {
          try {
            if (this.onUserLoggedOut) {
              void this.onUserLoggedOut();
            }
          } catch (e) {
            console.warn('onUserLoggedOut handler threw', e);
          }
          break;
        }

        case 'error':
          const errorMessage =
            payload && typeof payload === 'object' && 'message' in payload
              ? (payload as { message: string }).message
              : 'Unknown error';
          console.error('Bridge error:', errorMessage);
          break;
        case 'countryChange': {
          try {
            if (this.onCountryChange) {
              void this.onCountryChange(payload as string);
            }
          } catch (e) {
            console.warn('onCountryChange handler threw', e);
          }
          break;
        }

        case 'scroll': {
          try {
            if (this.onScrollDetected) {
              void this.onScrollDetected(payload as number);
            }
          } catch (e) {
            console.warn('onScrollDetected handler threw', e);
          }
          break;
        }
        case 'cartOpen': {
          try {
            if (this.onCartOpen) {
              void this.onCartOpen();
            }
          } catch (e) {
            console.warn('onCartOpen handler threw', e);
          }
          break;
        }

        case 'navigateToNativeScreen': {
          try {
            if (this.onNavigateToNativeScreen) {
              void this.onNavigateToNativeScreen(payload as NativeScreen);
            }
          } catch (e) {
            console.warn('onNavigateToNativeScreen handler threw', e);
          }
          break;
        }

        case 'actorIdResolved': {
          try {
            if (this.onActorIdResolved) {
              await this.onActorIdResolved(payload as ActorIdPayload);
            }
          } catch (e) {
            console.warn('onActorIdResolved handler threw', e);
          }
          break;
        }

        default:
          this.devDebugConsole(`Unknown message action: ${action}`);
      }
    } catch (error) {
      // Silently ignore parsing errors for non-bridge messages
      this.devDebugConsole(`Failed to parse message: ${message.nativeEvent.data}, error: ${error}`);
    }
  }

  // Utilities for cookie handling
  public static parseCookieString(cookieString: string): Array<{ name: string; value: string }> {
    return cookieString
      .split(';')
      .map((c) => c.trim())
      .filter(Boolean)
      .reduce<Array<{ name: string; value: string }>>((acc, pair) => {
        const eqIndex = pair.indexOf('=');
        if (eqIndex > -1) {
          const name = pair.slice(0, eqIndex).trim();
          const value = pair.slice(eqIndex + 1).trim();
          if (name) {
            acc.push({ name, value });
          }
        }
        return acc;
      }, []);
  }

  public static regionCookieInjection(params: { country: string; countryKey: string }): string {
    const { country, countryKey } = params;
    const lines: string[] = [];
    if (country) {
      const encoded = encodeURIComponent(country);
      lines.push(
        `console.log('[Bridge] Setting ${countryKey}=${country}');`,
        `document.cookie = '${countryKey}='+decodeURIComponent('${encoded}')+'; path=/; max-age=31536000';`,
      );
    }
    return lines.length > 0 ? lines.join('\n') : `// no region cookies to set`;
  }

  public static cartSessionCookieInjection(params: { name: string; value: string }): string {
    const { name, value } = params;
    return `
      document.cookie = '${name}=${value}; path=/; max-age=31536000';
    `;
  }

  // Expose a global function for web to call: window.__ssCartUpdated(payload)
  public static exposeCartUpdatedGlobal(): string {
    return `
      try {
        window.__ssCartUpdated = function () {
          try {
            const message = { action: 'cartUpdated' };
            if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
              window.ReactNativeWebView.postMessage(JSON.stringify(message));
            }
          } catch (e) {
            console.log('cartUpdated post failed', e);
          }
        };
      } catch (_) {}
    `;
  }

  // Expose a global function for web to call: window.__ssUserLoggedOut()
  public static exposeUserLoggedOutGlobal(): string {
    return `
      try {
        window.__ssUserLoggedOut = function () {
          try {
            const message = { action: 'userLoggedOut' };
            if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
              window.ReactNativeWebView.postMessage(JSON.stringify(message));
            }
          } catch (e) {
            console.log('userLoggedOut post failed', e);
          }
        };
      } catch (_) {}
    `;
  }

  /**
   * Expose a global function for native to inject actorId into WebView cookies.
   * Dispatches a CustomEvent so the web PersonalizationClient can react.
   *
   * @param storageKeys - Cookie key names for personalization identity.
   *   Pass PERSONALIZATION_STORAGE_KEYS from personalization/core/constants.
   */
  public static exposeActorIdReceiverGlobal(storageKeys: {
    ACTOR_ID: string;
    SESSION_ID: string;
    SESSION_TS: string;
  }): string {
    return `
      try {
        window.__ssSetActorId = function(opts) {
          try {
            document.cookie = '${storageKeys.ACTOR_ID}=' + encodeURIComponent(opts.actorId) + '; path=/; max-age=31536000; secure; samesite=Lax';
            document.cookie = '${storageKeys.SESSION_ID}=' + encodeURIComponent(opts.sessionId) + '; path=/; secure; samesite=Lax';
            document.cookie = '${storageKeys.SESSION_TS}=' + String(opts.sessionTimestamp) + '; path=/; secure; samesite=Lax';
            window.dispatchEvent(new CustomEvent('ss:actorIdInjected', {
              detail: opts
            }));
          } catch (e) {}
        };
      } catch (_) {}
    `;
  }

  /**
   * Expose analytics platform config to WebView.
   * WebView's WebAnalyticsPlatformProvider reads these via BridgeApi.getAnalyticsConfig().
   */
  public static exposeAnalyticsConfigGlobal(params: AnalyticsConfig): string {
    return `
      try {
        window.__ssAnalyticsConfig = ${JSON.stringify(params)};
      } catch (_) {}
    `;
  }

  /**
   * Read analytics config injected by native app.
   * Returns config if running inside WebView, undefined otherwise.
   */
  public static getAnalyticsConfig(): AnalyticsConfig | undefined {
    if (typeof window === 'undefined') return undefined;
    const config = (window as Window & { __ssAnalyticsConfig?: unknown })
      .__ssAnalyticsConfig;
    if (!isAnalyticsConfig(config)) return undefined;
    return config;
  }

  public static exposeCountryChangeGlobal(): string {
    return `
      try {
        window.__ssCountryChange = function (country) {
          try {
            const message = { action: 'countryChange', payload: country };
  
            if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
              window.ReactNativeWebView.postMessage(JSON.stringify(message));
            }
          } catch (e) {
            console.log('countryChange post failed', e);
          }
        };
      } catch (_) {}
    `;
  }

  // Add scroll event listener for header animation
  public static detectScroll(): string {
    return `
      let lastScrollY = 0;
      const throttle = (func, delay) => {
        let timeoutId;
        let lastExecTime = 0;
        return function (...args) {
          const currentTime = Date.now();
          if (currentTime - lastExecTime > delay) {
            func.apply(this, args);
            lastExecTime = currentTime;
          } else {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
              func.apply(this, args);
              lastExecTime = Date.now();
            }, delay - (currentTime - lastExecTime));
          }
        };
      };
      
      const handleScroll = throttle(() => {
        const scrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        if (Math.abs(scrollY - lastScrollY) > 1) { // Only send if scroll changed significantly
          try {
            if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
              window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'scroll', payload: scrollY }));
            }
            lastScrollY = scrollY;
          } catch (e) {
            console.log('scroll detection post failed', e);
          }
        }
      }, 16); // 60fps throttling
      
      window.addEventListener('scroll', handleScroll, { passive: true });
      document.addEventListener('scroll', handleScroll, { passive: true });
    `;
  }

  // Handle GDPR cookie banner based on ATT tracking permission status
  //TODO: find a way to handle this instead of using a long string function
  // https://github.com/SkinSeoul/SkinSeoulFront/pull/633#discussion_r2501866352
  public static handleGDPRBanner(params: { trackingStatus: string | null }): string {
    const { trackingStatus } = params;
    return `(function handleGDPRBanner() {
        const trackingStatus = '${trackingStatus || 'null'}';
        
        if (trackingStatus === 'null') {
          return;
        }
        
        const getCookie = (name) => {
          const value = '; ' + document.cookie;
          const parts = value.split('; ' + name + '=');
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };
        
        const mooveGdprPopup = getCookie('moove_gdpr_popup');
        if (mooveGdprPopup) {
          return;
        }
        
        let isHandled = false;
        let bannerObserver = null;
        
        const setupBannerObserver = () => {
          const banner = document.getElementById('moove_gdpr_cookie_info_bar');
          if (!banner) {  
            return;
          }
          
          bannerObserver = new MutationObserver((mutations) => {
            if (isHandled) {
              return;
            }
            
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const newClass = banner.getAttribute('class') || '';
                const isVisible = newClass.indexOf('moove-gdpr-info-bar-hidden') === -1;
                
                if (isVisible && !isHandled) {
                  if (!isHandled) {
                    handleBanner();
                  }
                }
              }
            });
          });
          
          bannerObserver.observe(banner, {
            attributes: true,
            attributeOldValue: false,
            attributeFilter: ['class']
          });
        };
        
        const handleBanner = () => {
          if (isHandled) {
            return;
          }
          
          const banner = document.getElementById('moove_gdpr_cookie_info_bar');
          
          if (!banner || trackingStatus === 'undetermined') {
            return;
          }
          
          isHandled = true;
          
          if (trackingStatus === 'granted') {
            const acceptButton = banner.querySelector('.moove-gdpr-infobar-allow-all');
            if (acceptButton) {
              acceptButton.click();
              banner.style.display = 'none';
            } 
          } else if (trackingStatus === 'denied') {
            const rejectButton = banner.querySelector('.moove-gdpr-infobar-reject-btn');
            if (rejectButton) {
              rejectButton.click();
              banner.style.display = 'none';
            }
          } else {
            banner.style.display = 'none';
          }
          
          if (bannerObserver) {
            bannerObserver.disconnect();
            bannerObserver = null;
          }
        };
        
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            setupBannerObserver();
          });
        } else {
          setupBannerObserver();
        }
      })();
    `;
  }

  /**
   * Detect clicks on WordPress add-to-cart buttons and notify native app
   * Uses event delegation to handle dynamically added elements
   */
  public static detectAddToCartClicks(params: { selector: string }): string {
    const { selector } = params;
    return `
      (function() {
        document.addEventListener('click', function(e) {
          var addToCartButton = e.target.closest('${selector}');
          if (addToCartButton && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
            // Delay to allow WordPress to process add-to-cart
            setTimeout(function() {
              window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'cartUpdated' }));
            }, 500);
          }
        }, { passive: true });
      })();
    `;
  }
}
